import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Button,
  InputGroup,
  Form,
  Row,
  Col,
  Container,
  Spinner,
  Badge,
  Tabs,
  Tab,
  Modal,
  Card
} from 'react-bootstrap';
import {  FaSearch, FaBoxOpen, FaEye, FaFileExport, FaTrash } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import BASE_URL, { BACKEND_URL } from '../config';
import './AdminLayout.css';

const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const formatDateTime = (dt) => {
    if (!dt) return 'N/A';
    try {
      const d = new Date(dt);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      const time = d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      return `${day}-${month}-${year}, ${time}`;
    } catch {
      return 'Invalid Date';
    }
};

const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'reconciled':
        return 'success';
      case 'verified':
        return 'info';
      case 'submitted':
        return 'primary';
      case 'collected':
        return 'success';
      case 'assigned':
        return 'warning';
      case 'pending':
      case 'created':
        return 'warning';
      case 'cancelled':
      case 'canceled':
        return 'danger';
      default:
        return 'secondary';
    }
};

// View Modal Component defined outside to prevent re-creation on render
const ViewModalContent = ({ show, handleClose, viewItem }) => {
    // Flatten attachments
    const attachments = [];
    if (viewItem && viewItem.attachments) {
        Object.keys(viewItem.attachments).forEach(key => {
            if (Array.isArray(viewItem.attachments[key])) {
                viewItem.attachments[key].forEach(file => {
                    attachments.push({...file, type: key});
                });
            }
        });
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            {viewItem ? (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>{viewItem.type} Details #{viewItem.displayUuid}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={6}><strong>Type:</strong> <Badge bg={viewItem.type === 'Payment' ? 'primary' : 'info'}>{viewItem.type}</Badge></Col>
                            <Col md={6}><strong>Customer:</strong> {viewItem.displayCustomer}</Col>
                            <Col md={6}><strong>Branch:</strong> {viewItem.displayBranch}</Col>
                            <Col md={6}><strong>Amount:</strong> AED {viewItem.amount}</Col>
                            <Col md={6}><strong>Method:</strong> {viewItem.method}</Col>
                            <Col md={6}><strong>Status:</strong> <Badge bg={getStatusVariant(viewItem.status)}>{viewItem.status}</Badge></Col>
                            <Col md={6}><strong>Driver:</strong> {viewItem.displayDriver || 'N/A'}</Col>
                            <Col md={6}><strong>Created At:</strong> {formatDateTime(viewItem.created_at)}</Col>
                            {viewItem.notes && <Col md={12}><strong>Notes:</strong> {viewItem.notes}</Col>}
                            
                            <Col md={12}>
                                <h6 className="mt-3">Attachments</h6>
                                {attachments.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        {attachments.map((file, idx) => (
                                            <Button 
                                                key={idx} 
                                                variant="outline-primary" 
                                                size="sm"
                                                onClick={() => window.open(`${BACKEND_URL}/${file.path}`, '_blank')}
                                            >
                                                <FaBoxOpen className="me-1"/> {file.type}
                                            </Button>
                                        ))}
                                    </div>
                                ) : <p className="text-muted">No attachments</p>}
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>Close</Button>
                    </Modal.Footer>
                </>
            ) : (
                <Modal.Body className="text-center p-5">
                    <Spinner animation="border" />
                </Modal.Body>
            )}
        </Modal>
    );
};

const AdminPayAndCollect = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('pending');
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewItem, setViewItem] = useState(null);
    
    // Pagination state
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(100);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isFetchingRef = useRef(false);

    // Fetch logic for a single page
    const fetchPage = async (endpoint, page, rowsPerPage, status) => {
        const params = { page, per_page: rowsPerPage };
        if (status && status !== 'all') params.status = status;
        
        console.log(`Fetching ${endpoint} with params:`, params);

        try {
            const res = await authAxios.get(endpoint, { params });
            
            // Handle different response structures
            let data = [];
            let meta = {};

            if (res.data?.data && Array.isArray(res.data.data)) {
                // Structure: { data: [...], meta: {...} } or { data: [...], last_page: ... }
                data = res.data.data;
                meta = res.data.meta || res.data;
            } else if (res.data?.data?.data && Array.isArray(res.data.data.data)) {
                 // Structure: { data: { data: [...] } }
                 data = res.data.data.data;
                 meta = res.data.data;
            } else if (Array.isArray(res.data)) {
                // Structure: [...]
                data = res.data;
                meta = {};
            } else {
                // Fallback
                data = [];
                meta = {};
            }
            
            console.log(`Fetched ${data.length} items from ${endpoint} for status ${status}`);

            return {
                data,
                total: meta?.total || data.length,
                lastPage: meta?.last_page || 1,
                currentPage: meta?.current_page || page
            };
        } catch (e) {
            console.error(`Error fetching ${endpoint}`, e);
            return { data: [], total: 0, lastPage: 1, currentPage: page };
        }
    };

    const fetchAllDataSequentially = async (tab) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setLoading(true);
        setDataList([]);
        
        try {
            // Determine statuses based on tab
            let paymentStatus = null;
            let collectionStatus = null;

            switch(tab) {
                case 'pending':
                    paymentStatus = null;
                    collectionStatus = 'created';
                    break;
                case 'assigned':
                    collectionStatus = 'assigned'; 
                    break;
                case 'collected':
                    collectionStatus = 'collected';
                    break;
                case 'submitted':
                    paymentStatus = 'submitted';
                    collectionStatus = 'submitted';
                    break;
                case 'verified':
                    paymentStatus = 'verified';
                    collectionStatus = 'verified';
                    break;
                case 'reconciled':
                    paymentStatus = 'reconciled';
                    collectionStatus = 'reconciled';
                    break;
                case 'cancelled':
                    paymentStatus = 'cancelled';
                    collectionStatus = 'cancelled';
                    break;
                default:
                    paymentStatus = tab;
                    collectionStatus = tab;
            }

            console.log(`Fetching data for tab: ${tab} | Payment Status: ${paymentStatus} | Collection Status: ${collectionStatus}`);

            // Helper to fetch all pages for an endpoint
            const fetchAllPages = async (endpoint, status, type) => {
                if (!status) return [];
                let page = 1;
                let allItems = [];
                let lastPage = 1;
                
                // First page
                const first = await fetchPage(endpoint, page, perPage, status);
                allItems = [...first.data];
                lastPage = first.lastPage;

                // Remaining pages
                if (lastPage > 1) {
                    setIsLoadingMore(true);
                    for (page = 2; page <= lastPage; page++) {
                        const next = await fetchPage(endpoint, page, perPage, status);
                        allItems = [...allItems, ...next.data];
                    }
                }
                
                // Normalize data
                return allItems.map(item => ({
                    ...item,
                    type: type,
                    displayUuid: type === 'Payment' ? item.uuid : item.code,
                    displayCustomer: type === 'Payment' ? item.customerName : item.customer_name,
                    displayBranch: type === 'Payment' ? item.branchName : item.showroom_name,
                    displayDriver: type === 'Payment' ? item.driver?.name : item.driver_name,
                    attachments: item.attachments || {}
                }));
            };

            const [paymentsData, collectionsData] = await Promise.all([
                fetchAllPages('/payments', paymentStatus, 'Payment'),
                fetchAllPages('/collections', collectionStatus, 'Collection')
            ]);

            const combined = [...paymentsData, ...collectionsData];
            console.log(`Combined data for ${tab}:`, combined);
            // Sort by created_at desc
            combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            
            setDataList(combined);
            setTotalRows(combined.length);

        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
            isFetchingRef.current = false;
        }
    };

    useEffect(() => {
        fetchAllDataSequentially(activeTab);
    }, [activeTab]);

    const filteredData = useMemo(() => {
        if (!search) return dataList;
        const q = search.toLowerCase();
        return dataList.filter(p => 
            p.displayUuid?.toLowerCase().includes(q) ||
            p.displayCustomer?.toLowerCase().includes(q) ||
            p.displayBranch?.toLowerCase().includes(q) ||
            p.displayDriver?.toLowerCase().includes(q) ||
            p.amount?.toString().includes(q)
        );
    }, [dataList, search]);

    const handleExportToExcel = () => {
        const data = filteredData.map(p => ({
            'Type': p.type,
            'ID': p.id,
            'Ref #': p.displayUuid,
            'Customer': p.displayCustomer,
            'Branch': p.displayBranch,
            'Amount': p.amount,
            'Method': p.method,
            'Status': p.status,
            'Driver': p.displayDriver || 'N/A',
            'Created At': formatDateTime(p.created_at)
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data");
        saveAs(new Blob([XLSX.write(wb, {bookType: 'xlsx', type: 'array'})]), `pay_collect_${activeTab}.xlsx`);
    };

    const handleViewClick = useCallback((row) => {
        setViewItem(row);
        setShowViewModal(true);
    }, []);

    const handleDeleteItem = useCallback(async (row) => {
        if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) return;

        try {
            const endpoint = row.type === 'Payment' ? `/payments/${row.id}` : `/collections/${row.id}`;
            await authAxios.delete(endpoint);
            setDataList(prev => prev.filter(item => !(item.id === row.id && item.type === row.type)));
            alert('Deleted successfully.');
        } catch (err) {
            console.error('Error deleting record:', err.response || err);
            alert(err.response?.data?.message || 'Failed to delete record.');
        }
    }, []);

    const columns = useMemo(() => [
        { name: '#', selector: (row, index) => index + 1, width: '60px' },
        { 
            name: 'Type', 
            selector: row => row.type, 
            sortable: true, 
            width: '100px',
            cell: row => <Badge bg={row.type === 'Payment' ? 'primary' : 'info'}>{row.type}</Badge>
        },
        { name: 'Ref #', selector: row => row.displayUuid, sortable: true, width: '140px' },
        { name: 'Customer', selector: row => row.displayCustomer, sortable: true, wrap: true },
        { name: 'Branch', selector: row => row.displayBranch, sortable: true },
        { name: 'Amount', selector: row => row.amount, sortable: true, cell: row => <strong>AED {row.amount}</strong> },
        { name: 'Method', selector: row => row.method, sortable: true },
        { name: 'Status', selector: row => row.status, sortable: true, cell: row => <Badge bg={getStatusVariant(row.status)}>{row.status}</Badge> },
        { name: 'Driver', selector: row => row.displayDriver || 'N/A', sortable: true },
        { name: 'Created At', selector: row => formatDateTime(row.created_at), sortable: true, width: '160px' },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-1">
                    <Button size="sm" variant="info" onClick={() => handleViewClick(row)} title="View">
                        <FaEye />
                    </Button>
                    {['created', 'assigned', 'assign_to_driver', 'assigned to driver'].includes(row.status?.toLowerCase()) && (
                        <Button size="sm" variant="danger" onClick={() => handleDeleteItem(row)} title="Delete">
                            <FaTrash />
                        </Button>
                    )}
                </div>
            ),
	    button: true,
            ignoreRowClick: true
        }
    ], [handleViewClick,handleDeleteItem]);

    const tabs = [
        { key: 'pending', title: 'Pending / Created' },
        { key: 'assigned', title: 'Assigned' },
        { key: 'collected', title: 'Collected' },
        { key: 'submitted', title: 'Submitted' },
        { key: 'verified', title: 'Verified' },
        { key: 'reconciled', title: 'Reconciled' },
        { key: 'cancelled', title: 'Cancelled' }
    ];

    return (
        <div className='border p-2 rounded' style={{ background: '#fff' }}>
            <Container fluid className='p-4'>
                <h3 className="text-center mb-4">Payment & Collection Management</h3>
                
                <Row className="mb-3 align-items-center">
                    <Col md={8}>
                        <InputGroup style={{ maxWidth: '300px' }}>
                            <InputGroup.Text><FaSearch /></InputGroup.Text>
                            <Form.Control 
                                placeholder="Search..." 
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={4} className="text-end">
                        <Button variant="success" size="sm" onClick={handleExportToExcel}>
                            <FaFileExport className="me-1"/> Export to Excel
                        </Button>
                    </Col>
                </Row>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-3"
                    fill
                    style={{ background: '#dbe6ff' }}
                >
                    {tabs.map(tab => (
                        <Tab eventKey={tab.key} title={tab.title} key={tab.key}>
                            {activeTab === tab.key && (
                                <div style={{ height: '50vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    {filteredData.length > 0 && (
                                        <div className="text-end mt-0" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', flexShrink: 0, padding: '4px 8px' }}>
                                            <small className="text-muted">
                                                Showing <strong>{filteredData.length}</strong> items
                                            </small>
                                        </div>
                                    )}
                                    <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                                    <DataTable
                                        columns={columns}
                                        data={filteredData}
                                        pagination={false}
                                        highlightOnHover
                                        striped
                                        dense
                                        persistTableHead
                                        fixedHeader
                                        fixedHeaderScrollHeight='45vh'
                                        progressPending={loading}
                                        progressComponent={<div className="p-5 text-center"><Spinner animation="border" variant="primary" /></div>}
                                        noDataComponent={<div className="p-5 text-center text-muted">No records found</div>}
                                        customStyles={{
                                            table: {
                                                style: {
                                                    tableLayout: 'auto',
                                                    width: '100%',
                                                    minWidth: '100%',
                                                },
                                            },
                                            tableWrapper: {
                                                style: {
                                                    width: '100%',
                                                    height: '100%',
                                                    overflowX: 'auto',
                                                    overflowY: 'auto',
                                                    position: 'relative',
                                                },
                                            },
                                            headRow: {
                                                style: {
                                                    backgroundColor: '#cce5ff',
                                                    color: '#004085',
                                                    borderBottomStyle: 'solid',
                                                    borderBottomWidth: '2px',
                                                    borderBottomColor: '#dee2e6',
                                                }
                                            },
                                            headCells: {
                                                style: {
                                                    backgroundColor: '#cce5ff',
                                                    color: '#004085',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    paddingLeft: '8px',
                                                    paddingRight: '8px',
                                                },
                                            },
                                            cells: {
                                                style: {
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal',
                                                },
                                            },
                                        }}
                                    />
                                    </div>
                                    {isLoadingMore && <div className="text-center p-2 text-muted"><Spinner size="sm" animation="border"/> Loading more...</div>}
                                </div>
                            )}
                        </Tab>
                    ))}
                </Tabs>

                <ViewModalContent 
                    show={showViewModal} 
                    handleClose={() => setShowViewModal(false)} 
                    viewItem={viewItem} 
                />
            </Container>
        </div>
    );
};

export default AdminPayAndCollect;
